import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type { ReactStore } from './types'
import { REACT_STORE_TYPE } from './types'
import { withoutActWarning } from '@w/utils-fe'

type Path = string
type Listener = () => void
type CloneFn = (value: unknown) => unknown

/**
 * Deep clone that preserves promises, functions, symbols, and other non-cloneable objects
 * Falls back to reference for objects that can't be cloned
 *
 * Use this with createStore when your state contains non-cloneable objects:
 * @example
 * ```typescript
 * const store = createStore(initialState, { cloneFn: deepClone })
 * ```
 */
export function deepClone<T>(value: T, seen = new WeakMap()): T {
  // Primitives
  if (value === null || typeof value !== 'object') {
    return value
  }

  // Already cloned (circular reference)
  if (seen.has(value)) {
    // oxlint-disable-next-line typescript/no-unsafe-return -- WeakMap generic is unknown by design; circular-ref lookup returns original typed value
    return seen.get(value)
  }

  // Special objects that should be preserved by reference
  if (
    value instanceof Promise ||
    value instanceof Date ||
    value instanceof RegExp ||
    value instanceof Error ||
    value instanceof Map ||
    value instanceof Set ||
    value instanceof WeakMap ||
    value instanceof WeakSet ||
    typeof value === 'function' ||
    (typeof AbortSignal !== 'undefined' && value instanceof AbortSignal) ||
    (typeof AbortController !== 'undefined' && value instanceof AbortController)
  ) {
    return value
  }

  // Arrays
  if (Array.isArray(value)) {
    const cloned: unknown[] = []
    seen.set(value, cloned)
    for (const item of value) {
      cloned.push(deepClone(item, seen))
    }
    return cloned as unknown as T
  }

  // Plain objects
  const cloned: Record<string | symbol, unknown> = {}
  seen.set(value, cloned)

  // Clone own properties (including symbols)
  for (const key of Reflect.ownKeys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key)
    if (descriptor?.enumerable) {
      cloned[key] = deepClone(
        (value as Record<string | symbol, unknown>)[key],
        seen,
      )
    }
  }

  return cloned as T
}

class ReactStoreImpl<
  Value,
  Action = Value | ((prev: Value) => Value),
> implements ReactStore<Value, Action> {
  _listeners: Map<Path, Set<Listener>>
  _current: Value
  _path: Path
  _cloneFn: CloneFn
  // oxlint-disable-next-line typescript/no-explicit-any -- Root store can have any action type
  _rootStore?: ReactStoreImpl<unknown, any>

  constructor(
    initialValue: Value,
    path: Path = '',
    // oxlint-disable-next-line typescript/no-explicit-any -- Root store can have any action type
    rootStore?: ReactStoreImpl<unknown, any>,
    cloneFn?: CloneFn,
  ) {
    this._current = initialValue
    this._path = path
    this._rootStore = rootStore
    this._cloneFn = rootStore?._cloneFn ?? cloneFn ?? structuredClone
    // oxlint-disable-next-line typescript/no-unsafe-assignment -- rootStore uses `any` generic for action type; listener map is structurally `Map<string, Set<Listener>>`
    this._listeners = rootStore?._listeners ?? new Map()
  }

  getState(): Value {
    const rootCurrent = this._rootStore?._current ?? this._current
    if (!this._path) {
      return rootCurrent as Value
    }
    const parts = this._path.split('/')
    let current: unknown = rootCurrent
    for (const part of parts) {
      if (current instanceof Map) {
        current = current.get(part)
      } else if (current && typeof current === 'object') {
        current = (current as Record<string, unknown>)[part]
      } else {
        return undefined as unknown as Value
      }
    }
    return current as Value
  }

  private updateAtPath(root: unknown, path: string, value: unknown): unknown {
    if (!path) {
      return value
    }

    const updated = this._cloneFn(root)
    let current: unknown = updated
    const parts = path.split('/')

    // Navigate to parent of target
    for (let i = 0; i < parts.length - 1; i++) {
      // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- noUncheckedIndexedAccess not enabled repo-wide but loop bound guarantees defined
      const part = parts[i] as string
      if (current instanceof Map) {
        current = current.get(part)
      } else if (current && typeof current === 'object') {
        current = (current as Record<string, unknown>)[part]
      }
    }

    // Update the target key
    // oxlint-disable-next-line typescript/no-unnecessary-type-assertion, unicorn/prefer-at -- loop bound guarantees defined; prefer-at would require the same assertion
    const lastPart = parts[parts.length - 1] as string
    if (current instanceof Map) {
      current.set(lastPart, value)
    } else if (current && typeof current === 'object') {
      ;(current as Record<string, unknown>)[lastPart] = value
    }

    return updated
  }

  refresh = (path: Path = '') => {
    const targetParts = path ? path.split('/') : []

    const isPrefix = (a: string[], b: string[]) => {
      if (a.length > b.length) return false
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
          return false
        }
      }
      return true
    }

    withoutActWarning(() => {
      this._listeners.forEach((listeners, listenerPath) => {
        const listenerParts = listenerPath ? listenerPath.split('/') : []
        const isMatch =
          listenerParts.length === 0 ||
          targetParts.length === 0 ||
          isPrefix(listenerParts, targetParts) ||
          isPrefix(targetParts, listenerParts)

        if (!isMatch) {
          return
        }

        for (const listener of listeners) {
          try {
            listener()
          } catch (error) {
            // Isolate errors - don't break other listeners
            console.error('[ReactStore] Listener error:', error)
          }
        }
      })
    })
  }

  update = (action: Action) => {
    const rootStore = this._rootStore ?? this
    const currentValue = this.getState()

    // Resolve the new value (handle callback)
    const newValue =
      typeof action === 'function'
        ? (action as (prev: Value) => Value)(currentValue)
        : action

    // Update root store
    rootStore._current = this.updateAtPath(
      rootStore._current,
      this._path,
      newValue,
    )
    rootStore.refresh(this._path)
  }

  // oxlint-disable-next-line typescript/no-explicit-any -- Select method type-casted to ReactStore's select signature
  select = ((key: any): any => {
    const newPath = this._path ? `${this._path}/${String(key)}` : String(key)
    const rootStore = this._rootStore ?? this

    return new ReactStoreImpl(
      // oxlint-disable-next-line typescript/no-explicit-any -- Placeholder value, computed dynamically via getCurrentValue
      undefined as any,
      newPath,
      // oxlint-disable-next-line typescript/no-explicit-any -- Root store can have any action type
      rootStore as ReactStoreImpl<unknown, any>,
    )
  }) as ReactStore<Value, Action>['select']

  // ReactStore type marker - satisfies the symbol index signature
  readonly [REACT_STORE_TYPE] = undefined as never
}

export interface CreateStoreOptions {
  /**
   * Custom clone function for immutable updates
   * Defaults to structuredClone
   * Use deepClone export for cloning that preserves promises, functions, etc.
   */
  cloneFn?: CloneFn
}

/**
 * Creates a React Store
 *
 * @example
 * ```typescript
 * const store = createStore({ count: 0 })
 *
 * // Update with new value
 * store.update({ count: 1 })
 *
 * // Update with callback
 * store.update(prev => ({ count: prev.count + 1 }))
 *
 * // With custom clone function
 * import { createStore, deepClone } from '@w/react-store'
 * const store = createStore({ data: promise }, { cloneFn: deepClone })
 * ```
 */
export function createStore<Value>(
  initialValue: Value,
  options?: CreateStoreOptions,
): ReactStore<Value, Value | ((prev: Value) => Value)>

export function createStore<Value>(
  initialValue: Value,
  options?: CreateStoreOptions,
): ReactStore<Value, Value | ((prev: Value) => Value)> {
  return new ReactStoreImpl(initialValue, '', undefined, options?.cloneFn)
}

/**
 * React hook to subscribe to a store
 *
 * @param store - The React Store to subscribe to
 *
 * @example
 * ```typescript
 * // Subscribe to entire store
 * const state = useStore(store)
 *
 * // Subscribe to nested path using store.select()
 * const usersStore = store.select('users')
 * const users = useStore(usersStore)
 * ```
 */
export function useStore<Value>(
  // oxlint-disable-next-line typescript/no-explicit-any -- ReactStore action type is generic
  store: ReactStore<Value, any>,
): Value {
  // oxlint-disable-next-line typescript/no-explicit-any -- storeImpl cast needs any
  const storeImpl = store as unknown as ReactStoreImpl<Value, any>

  const getValue = useCallback(() => storeImpl.getState(), [storeImpl])
  const [cache, setCache] = useState(getValue)
  const cacheRef = useRef(cache)

  useLayoutEffect(() => {
    let unsubscribed = false
    const emit = () => {
      if (unsubscribed) {
        return
      }
      const next = getValue()
      if (!Object.is(next, cacheRef.current)) {
        cacheRef.current = next
        setCache(next)
      }
    }

    // Catch updates that landed between render and subscription
    emit()

    const path = storeImpl._path
    const listeners = storeImpl._listeners.get(path) ?? new Set<Listener>()
    storeImpl._listeners.set(path, listeners)
    listeners.add(emit)
    return () => {
      unsubscribed = true
      listeners.delete(emit)
      if (listeners.size === 0) storeImpl._listeners.delete(path)
    }
  }, [storeImpl, getValue])

  return cache
}
